import ServiceFactory from '../service';
import classify, { GROUP_ORDER } from './classify';
import REGISTRY from './registry';
import rankResults from './rank';
import account from '../data/account';
import searchAccess, { sourceAvailable } from './access';
const ENDPOINT_TIMEOUT_MS = 6000;
const DEBOUNCE_MS = 300;
const DEFAULT_LIMIT = 5; // rows per source in the dropdown; the /Search page asks for more

export default class SearchService {
  constructor() {
    this.controller = null;
    this.debounceTimer = null;
  }
  cancel() {
    if (this.controller) {
      this.controller.abort();
      this.controller = null;
    }
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
  }
  search(rawTerm, options = {}) {
    this.cancel();
    return new Promise(resolve => {
      this.debounceTimer = setTimeout(async () => {
        const result = await this._run(rawTerm, options);
        resolve(result);
      }, options.debounce ?? DEBOUNCE_MS);
    });
  }
  async _run(rawTerm, options) {
    const classification = classify(rawTerm, {
      account: account.get(),
      currentPath: options.currentPath || (typeof location !== 'undefined' ? location.pathname : '')
    });
    if (!classification.entities.length) {
      return {
        term: classification.term,
        classification,
        ranked: [],
        groups: {}
      };
    }
    this.controller = new AbortController();
    const signal = this.controller.signal;
    const limit = options.limit || DEFAULT_LIMIT;

    // Drop the sources this member can't open (Commerce section or the
    // nav's feature gate) BEFORE fetching, so they never produce a group —
    // and so nothing downstream (Enter's fallback) can navigate into them.
    const access = await searchAccess();
    if (signal.aborted) return {
      term: classification.term,
      classification,
      ranked: [],
      groups: {},
      aborted: true
    };
    classification.entities = classification.entities.filter(key => sourceAvailable(REGISTRY[key], access));
    if (!classification.entities.length) {
      return {
        term: classification.term,
        classification,
        ranked: [],
        groups: {}
      };
    }
    const fetches = classification.entities.map(key => this._fetchEntity(key, classification.term, signal, classification.context, limit, access));
    const results = await Promise.all(fetches);
    if (signal.aborted) return {
      term: classification.term,
      classification,
      ranked: [],
      groups: {},
      aborted: true
    };
    const groups = {};
    results.forEach(({
      entityKey,
      rows,
      config,
      error
    }) => {
      if (error) return;
      groups[entityKey] = {
        config,
        rows
      };
    });
    const locationHit = groups.locations && groups.locations.rows.length > 0 || groups.product_location && groups.product_location.rows.length > 0;
    let locationConfirmed = false;
    if (locationHit && !classification.entities.includes('market_trends')) {
      const extra = await this._fetchEntity('market_trends', classification.term, signal, classification.context, limit, access);
      if (!signal.aborted && !extra.error) {
        groups.market_trends = {
          config: extra.config,
          rows: extra.rows
        };
      }
      locationConfirmed = true;
    }
    await this._secondPhaseActivity(classification, groups, signal);
    const currentEntity = this._currentEntity(options.currentPath);
    const ranked = rankResults(groups, classification.term, currentEntity, GROUP_ORDER);
    return {
      term: classification.term,
      classification,
      ranked,
      groups,
      locationConfirmed
    };
  }
  async _secondPhaseActivity(classification, groups, signal) {
    const interactionsGroup = groups.interactions;
    if (!interactionsGroup || !interactionsGroup.rows.length) return;
    const kvRows = interactionsGroup.rows.map(row => ({
      title: row.title,
      type: row.type,
      _ctx: {
        activityType: row.type,
        activityName: String(row.title || '').replace(/ /g, '@')
      }
    }));
    groups.interaction_keyvalues = {
      config: REGISTRY.interaction_keyvalues,
      rows: kvRows
    };
  }
  async _fetchEntity(entityKey, term, signal, context, limit = DEFAULT_LIMIT, access = null) {
    const config = REGISTRY[entityKey];
    if (!config) return {
      entityKey,
      rows: [],
      error: new Error('unknown_entity')
    };
    if (!sourceAvailable(config, access)) return {
      entityKey,
      rows: [],
      error: new Error('not_available'),
      config
    };
    try {
      let rows;
      if (config.load) {
        // Sources with their own reader (browser-filtered lists, json-bigint ids).
        rows = await this._withTimeout(Promise.resolve(config.load(term, context, limit, access || {})), ENDPOINT_TIMEOUT_MS, signal);
      } else {
        const api = config.api(term, context, limit);
        if (!api) return {
          entityKey,
          rows: [],
          config
        };
        rows = await this._withTimeout(this._fetchOData(api, config.serviceType, signal), ENDPOINT_TIMEOUT_MS, signal);
      }
      const items = config.unwrap ? config.unwrap(rows) : Array.isArray(rows) ? rows : rows && rows.items ? rows.items : [];
      return {
        entityKey,
        rows: (items || []).slice(0, limit),
        config
      };
    } catch (err) {
      return {
        entityKey,
        rows: [],
        error: err,
        config
      };
    }
  }
  async _fetchOData(api, serviceType, signal) {
    const Service = ServiceFactory.getType(api, serviceType);
    const service = new Service();
    if (signal) {
      signal.addEventListener('abort', () => {
        if (service.abort) service.abort();
      });
    }
    return service.getAsync();
  }
  _withTimeout(promise, ms, signal) {
    return Promise.race([promise, new Promise((_, reject) => {
      const t = setTimeout(() => reject(new Error('timeout')), ms);
      if (signal) signal.addEventListener('abort', () => clearTimeout(t));
    })]);
  }
  _currentEntity(currentPath) {
    const path = currentPath || (typeof location !== 'undefined' ? location.pathname : '');
    if (path.startsWith('/Contacts')) return 'contacts';
    if (path.startsWith('/Customers')) return 'customers';
    if (path.startsWith('/Territory')) return 'reps';
    if (path.startsWith('/Quotes')) return 'quotes';
    if (path.startsWith('/Stores')) return 'stores';
    if (path.startsWith('/Reports')) return 'reports';
    if (path.startsWith('/Content')) return 'content_images';
    if (path.startsWith('/Product')) return 'products';
    if (path.startsWith('/Audience')) return 'audiences';
    if (path.startsWith('/Research/Company')) return 'company_analysis';
    if (path.startsWith('/Research/Trends')) return 'market_trends';
    if (path.startsWith('/Campaign')) return 'campaigns';
    if (path.startsWith('/Forecast/Product')) return 'forecast_product';
    if (path.startsWith('/Forecast/Similarity')) return 'forecast_similarity';
    if (path.startsWith('/Activity/Pages')) return 'pages';
    return null;
  }
}